import StateObserver from 'src/StateObserver';
import { createPersistentEmitter } from 'src/lib/Emitter';
import { RealtimeUserProperties } from 'src/lib/analytics/properties';
import { State } from 'src/state';
import * as Pet from 'src/replicant/pet';
import * as Inventory from 'src/replicant/inventory';
import * as Encyclopedia from 'src/replicant/encyclopedia';
import * as Level from 'src/replicant/level';
import { encodePacket } from 'src/replicant/utils/encoder';
import {
  canParticipateOnCurrentRace,
  getActiveRace,
  hasJoinedActiveRace,
} from 'src/replicant/match2/races/races.getters';
import {
  getCurrentLeaguePhase,
  canPlayLeague,
} from 'src/replicant/match2/leagues/leagues.getters';
import { hasJoinedLeague } from 'src/replicant/match2/leagues/leagues.getters';
import { ProductId, webIAPPrices } from 'src/replicant/iap/iap.ruleset';
import * as Tutorial from 'src/replicant/tutorial';
import { setUserProperties } from './analytics/utils';
import { isStreakBattleRoyaleActive } from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { getAnalyticsRaceGoal } from 'src/game/minigame/match2-timestep/races/races.sequence';
import { getSuperLollipopUserProperties } from 'src/replicant/superLollipop/superLollipop.getters';
import { getTimedRewardUserProperties } from 'src/replicant/match2/match2.analytics';
import { getWeeklyLeaderboardUserProperties } from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.getters';
import { getPuzzleWinsLast7Days } from 'src/replicant/puzzleWinCount/puzzleWinCount.getters';
import { getChampionsLeagueUserProperties } from 'src/game/championsLeague/championsLeague.utils';
import { GCInstant } from 'src/lib/gcinstant';
import features from 'src/replicant/ruleset/features';
import { isWebIapAvailable } from './iap/iap.util';
import { isIapReady } from 'src/game/shop/Helpers';

export default function setupUserProps() {
  const selector = (state: State): RealtimeUserProperties => {
    const now = StateObserver.now();
    const { user } = state;

    const matchRaceActive =
      canParticipateOnCurrentRace(user, now) && hasJoinedActiveRace(user, now);
    const matchRaceConfig = matchRaceActive
      ? getActiveRace(now)?.config
      : undefined;

    const match2Props = {
      realtimeEnergy: Inventory.getters.getEnergy(user, now),
      realtimeInMastersLeague: hasJoinedLeague(user),

      // Boosters
      realtimeHammer: user.match2.boosters.dart ?? 0,
      realtimeAirplane: user.match2.boosters.bullet ?? 0,
      realtimeCuprocket: user.match2.boosters.drill ?? 0,
      realtimePinwheel: user.match2.boosters.roulette ?? 0,

      // Powerups
      realtimeFirecracker: user.match2.powerups.rocket ?? 0,
      realtimeWaterballoon: user.match2.powerups.bomb ?? 0,
      realtimeLollipop: user.match2.powerups.cube ?? 0,

      // Races
      matchRaceActive,
      realtimeRaceGoal: matchRaceConfig
        ? getAnalyticsRaceGoal(matchRaceConfig)
        : undefined,

      // Fuji quest
      realtimeStreakQuest: isStreakBattleRoyaleActive(user, now)
        ? user.streak?.milestone
        : undefined,

      // Timed reward properties
      ...getTimedRewardUserProperties(user, now),

      // Puzzle wins
      lastExit7DayWins: getPuzzleWinsLast7Days(user, now).reduce(
        (acc, cur) => acc + cur,
        0,
      ),
    };

    const ltv = user.lifetimeValue + user.actualLTV;
    const purchaseHistory = StateObserver.replicant.getPurchaseHistory();
    return {
      $realtimeHasDisabledMessages: !user.settings.receiveMessages,
      $realtimeIsPurchaser: !!ltv,
      $realtimeLTV: ltv / 100,
      $realtimeLTVJPY: purchaseHistory.reduce(
        (sum, purchase) =>
          // webIAPPrices are in JPY
          sum + (webIAPPrices[purchase.product_id as ProductId] ?? 0),
        0,
      ),
      $realtimeLocale: user.settings.locale,
      $realtimePurchaseCount: purchaseHistory.length,
      $realtimeName: user.profile?.name,

      // Tutorial completion
      $realtimeIsTutorialCompleted: Tutorial.getters.isTutorialStepComplete(
        user,
        'puzzleCoinsCare',
      ), // Control tutorial(old one) and puzzleCoins case, same as 'careForPetsShown'

      // Pet state, age, stage etc...
      ...Pet.getters.getAnalyticsUserProperties(user, now),

      // stomach, love, dirtiness, stress etc...
      ...Pet.getters.getAnalyticsAttributeUserProperties(
        user,
        Math.floor(now / 60_000) * 60_000,
      ),

      settings_bgm: user.settings.music,
      settings_sfx: user.settings.sound,
      settings_lang: user.settings.locale,
      settings_notif: user.settings.receiveMessages,

      // Currency report
      bPoints: Inventory.getters.getPoints(user),
      freeGems: user.inventory.premium.free,
      paidGems: user.inventory.premium.paid,
      totalGems: Inventory.getters.getGems(user),
      energy: Inventory.getters.getEnergy(user, now),

      // Profile
      inGameProfileSet: !!user.publicProfile,
      publicProfileName: user.publicProfile?.name,
      maskedUID: encodePacket(user.id),

      // Guest mode
      realtimeIsGuest: GCInstant.platformID === 'line-guest',
      realtimeIsReplicantGuest: features(user).isGuestMode,

      // Level
      player_xp: user.level.xp,
      player_level: Level.getters.getCurrentLevel(user),

      // Highest pet age
      highest_age: user.highestPetAge,

      // Number of pets unlocked.
      pets_unlocked: Encyclopedia.getters.getUnlockedSpecies(user).length,

      // Number of decoration items owned by the player, excluding the default ones
      decorationItemsPurchased: Object.keys(user.decoration.purchased).length,

      // Number of accessory items owned by the player
      accessoryItemsPurchased: Object.keys(user.accessory.purchased).length,

      // Whether the player had purchased decoration items before the migration.
      // In this case migration scrip set  allowDecorationEditor to true.
      hadOldDecoration: user.progressionMigration
        ? user.allowDecorationEditor
        : undefined,

      // If user did progression migration and has decoration editor enabled
      // We will report what option user choose for decoration experience
      decorationMechanics: user.progressionMigration
        ? user.settings?.decorationEditor
          ? 'old'
          : 'new'
        : undefined,

      windowInnerWidth: window.innerWidth,
      windowInnerHeight: window.innerHeight,

      ...match2Props,
      ...getWeeklyLeaderboardUserProperties(user, now),
      ...getChampionsLeagueUserProperties(),

      // IAP properties
      realtimeIsShopAvailable: isIapReady(state) || isWebIapAvailable(),
    };
  };

  createPersistentEmitter(selector).addListener((props) =>
    setUserProperties(props),
  );
  // Also do a single update of the puzzle-related user properties on setup
  // These will not be be automatically updated on every state change, but only
  // after PuzzleStart and PuzzleFinish events.
  setPuzzleUserProps();
}

/**
 * These properties should get updated manually right after PuzzleStart and PuzzleFinish events.
 */
export function setPuzzleUserProps() {
  const user = StateObserver.getState().user;

  const match2Props = {
    realtimeMatchLevel: user.match2.level,
    realtimeBuruStreak: user.match2.buruStreak.value,
    realtimePuzzleWinStreak: user.match2.winStreak,
    realtimePuzzleLoseStreak: user.match2.losingStreak,

    // Leagues
    realtimeLeaguePhase: canPlayLeague(user)
      ? getCurrentLeaguePhase(user)
      : undefined,

    // Super lollipop properties
    ...getSuperLollipopUserProperties(user),
  };

  setUserProperties(match2Props);
}
